import { ClipboardCheck, FileSpreadsheet } from 'lucide-react';
import { lazyComponent } from '@/platform/lazyComponent';
import type { PlatformModule } from '@/platform/modules';
import { attendanceAthleteRoutes, attendanceRoutes } from './routes';

const AttendanceReport = lazyComponent(() => import('./reports/AttendanceReport').then((module) => module.AttendanceReport));

/**
 * Attendance: a register for training, matches and any other activity a sport runs. It stands on Athletes and Base — a
 * register's roster comes from Athletes' own teams and memberships, never a list of its own. Every route requires an
 * explicit attendance permission (deny by default); changing the activity types and statuses a register offers needs
 * `attendance_config.manage`, held only by an administrator unless it is given to someone else.
 */
export const attendanceModule: PlatformModule = {
  id: 'attendance',
  nav: [
    // Nested under the Athlete menu group (Athletes' own top item, `id: 'athletes'`), next to Player Profiles.
    { id: 'attendance', label: 'Attendance', to: '/attendance', icon: ClipboardCheck, permission: 'attendance.view', section: 'main', order: 20, parentId: 'athletes' },
    {
      id: 'athlete-reports',
      label: 'Report',
      to: '/attendance/report',
      icon: FileSpreadsheet,
      permission: 'attendance.report',
      section: 'main',
      order: 30,
      parentId: 'athletes',
      children: [
        { id: 'attendance-report', label: 'Attendance Report', to: '/attendance/report', icon: FileSpreadsheet, permission: 'attendance.report', section: 'main', order: 10 },
      ],
    },
  ],
  routes: attendanceRoutes,
  // The Attendance tab of the athlete's page, which Athletes owns (its route has the id `athlete`).
  routeExtensions: { athlete: attendanceAthleteRoutes },
  athleteTabs: [{ id: 'attendance', label: 'Attendance', path: 'attendance', permission: 'attendance.view', order: 80, icon: ClipboardCheck }],
  reports: [
    {
      id: 'attendance',
      slug: 'attendance',
      title: 'Attendance report',
      description: 'A filtered attendance report, by sport, season, team, activity, status and athlete, downloaded directly as Excel.',
      icon: FileSpreadsheet,
      permission: 'attendance.report',
      clinical: false,
      order: 60,
      component: AttendanceReport,
      showInReports: false,
    },
  ],
  access: {
    title: 'Attendance',
    order: 40,
    levels: [
      { id: 'viewer', label: 'Viewer', description: 'See attendance registers and history.', perms: ['attendance.view'] },
      {
        id: 'user',
        label: 'User',
        description: 'Also take attendance for a session, reopen and change a saved register, and preview and export the attendance report.',
        perms: ['attendance.mark', 'attendance.edit', 'attendance.report', 'attendance.export'],
      },
    ],
    extras: [{ id: 'configuration', label: 'Configuration', description: 'Change the activity types and statuses a register offers.', perms: ['attendance_config.manage'] }],
    permissions: {
      'attendance.view': 'See attendance registers and history',
      'attendance.mark': 'Take attendance for a session',
      'attendance.edit': 'Reopen and change a saved register',
      'attendance.report': 'Preview an attendance report',
      'attendance.export': 'Export an attendance report to Excel',
      'attendance_config.manage': 'Change the Attendance activity types and statuses',
    },
  },
};
